import moment from "moment"
import sortBy from "lodash.sortby"
import min from "lodash.min"
import uniqBy from "lodash.uniqby"
import upperFirst from "lodash.upperfirst"

import { ProductCategory, ProductGroup, Product } from "typings/products"
import { ExtendedLineItems } from "typings/pricing"
import { Option } from "typings/select-options"
import {
  DrugTypeEnum,
  DosageTypeEnum,
  DrugCategoryEnum,
  GetPricingLineItems,
  ActiveSubscriptionsFragmentActiveSubscriptions,
  GetPricingProduct,
  LineItemKindEnum,
  Product as _Product,
  JourneyKindEnum
} from "typings/types"

import { PRODUCT_DRUG_FACT_PATHS } from "pages/drug-facts"
import { CurrentPricing } from "graphql/smart-queries/user"
import { isExceptionProduct } from "pages/products/helpers/exception-products"
import { useProductsState } from "shared/context/products"

import { s3CoveUrl } from "routes"

/*
  Takes in a productGroup and creates options for the dosage-dropdown

  @return dosage-dropdown options where the value is the id of the corresponding product.
*/
export const createOptionsForDosage = (productGroup: ProductGroup): Option<number>[] => {
  // For OTD products we don't have options with empty amountValue
  const productsWithNoDosageAmount = productGroup.products.filter(
    p =>
      (!!p.dosageValue && !p.amountValue) ||
      (p.dosageType === DosageTypeEnum.OdtMg && !!p.dosageValue) ||
      (p.drugCategory === DrugCategoryEnum.Otc && !!p.dosageValue)
  )
  const sortedByDosageValue = sortBy(productsWithNoDosageAmount, "dosageValue")

  return sortedByDosageValue.map(p => ({
    label: `${p.dosageValue}mg`,
    value: p.id
  }))
}

export const filterProducts = (
  allProducts: ProductGroup[],
  { category = "all", needsPrescription }: { category?: ProductCategory; needsPrescription?: boolean }
): ProductGroup[] => {
  // Filter productGroups by category
  const productGroupsByCategory = allProducts.filter(productGroup => {
    if (category === "all") {
      return true
    } else {
      return productGroup.drugCategory === category
    }
  })

  if (needsPrescription === undefined) {
    return productGroupsByCategory
  } else {
    // Filter out productGroup products that don't match with needsPrescription
    const filteredProductGroups = productGroupsByCategory.map<ProductGroup>(productGroup => {
      const filteredProducts = productGroup.products.filter(product => product.needsPrescription === needsPrescription)

      return { ...productGroup, products: filteredProducts }
    })

    // Remove groups that don't have any products
    return filteredProductGroups.filter(productGroup => productGroup.products.length)
  }
}

/* TODO: should be pulled in dynamically */
export const fallbackConsultationPrice = 15

/* TODO: should be pulled in dynamically */
export const shippingPrice = 3

export const getMinDisplayedPriceForProductGroup = (productGroup: ProductGroup) => {
  const nonZeroPrices = productGroup.products
    .filter(p => isExceptionProduct(p.drugType, p) || !!p.price)
    .map(p => p.price!)

  if (nonZeroPrices.length > 0) {
    return min(nonZeroPrices)
  }

  throw new Error("Unexpected: missing price.")
}

export const getMinProductByPrice = (products: Product[]) => {
  const product = products.reduce((result: Product, item: Product) => {
    if (!result.price || !item.price) {
      return result
    }
    return result.price > item.price ? item : result
  })

  return product
}

/**
 * Determines whether or not the standard offer percentage-off applies to a product
 */
export const isPercentOffDiscount = (product: Product): boolean =>
  !isExceptionProduct(product.drugType, product) && product.drugCategory !== DrugCategoryEnum.Otc

/**
 * pricing param is from the rootQuery pricing field
 * returns object w/ amount + amountWithDiscount props for a product
 * @param pricing
 * @param productId
 */
export const getAmountAndDiscountedAmountForProduct = (pricing: CurrentPricing, productId: number) => {
  const productLineItems = pricing.lineItems.filter(
    li => li.kind === LineItemKindEnum.Product && li.product?.id === productId
  )
  if (!productLineItems || !productLineItems.length) {
    throw Error(`no pricing lineItem for product ${productId}`)
  }
  return {
    amount: productLineItems[0].amount,
    amountWithDiscount: productLineItems[0].amountWithDiscount
  }
}

export const getSubscriptionPriceForProductGroup = (productGroup: ProductGroup) => {
  const [recurringSubscriptionPrice] = productGroup.products.map(p => p.recurringProduct && p.recurringProduct.price!)

  if (recurringSubscriptionPrice) {
    return recurringSubscriptionPrice
  }

  throw new Error("Unexpected: missing recurring price.")
}

export const formatPrice = (price = 0) => `$${price.toFixed(2)}`.replace(/\.00$/, "")
export const formatPriceZeroIsFree = (price = 0) => (formatPrice(price) === "$0" ? "FREE" : formatPrice(price))

export const formatDosage = (displayedAmount = "", displayedDosage = "") => {
  // displayedDosage: "10.0mg pill"
  displayedDosage = displayedDosage.replace("Not sure", "").replace(".0", "").replace(" pill", "")
  // displayedAmount: "30 count"
  displayedAmount = displayedAmount.replace("Not sure", "").replace(" count", "ct")
  return (
    (displayedAmount ? displayedAmount : "") +
    (displayedDosage && displayedAmount ? ", " : "") +
    (displayedDosage ? displayedDosage : "")
  )
}

// We want to show tooltip only for ODT products currently
export const prepareTooltipText = (drugType: DrugTypeEnum, dosageType: DosageTypeEnum) => {
  if (dosageType !== DosageTypeEnum.OdtMg) {
    return undefined
  }

  switch (drugType) {
    case DrugTypeEnum.Ondansetron:
      return "This is an orally dissolving form of ondansetron."
    case DrugTypeEnum.Rizatriptan:
      return "This is an orally dissolving form of rizatriptan."
    case DrugTypeEnum.Rimegepant:
      return "Nurtec ODT is an orally dissolving tablet."
    default:
      return undefined
  }
}

/* todo: why is display name based off drugtype? */
export const displayedProductName = (drugType: DrugTypeEnum, dosageType: Maybe<DosageTypeEnum>) => {
  if (drugType === DrugTypeEnum.Ondansetron && dosageType === DosageTypeEnum.OdtMg) {
    return "Ondansetron ODT"
  } else if (drugType === DrugTypeEnum.Rizatriptan && dosageType === DosageTypeEnum.OdtMg) {
    return "Rizatriptan ODT"
  } else if (drugType === DrugTypeEnum.Coq10) {
    return "Coenzyme Q10"
  } else if (drugType === DrugTypeEnum.MigraineRelief) {
    return "Migraine Relief 24 Ct"
  } else if (drugType === DrugTypeEnum.Nerivio) {
    return "Nerivio Device"
  } else if (drugType === DrugTypeEnum.Rimegepant) {
    return "Nurtec™ ODT"
  } else if (drugType === DrugTypeEnum.Diclofenac) {
    return "CAMBIA®"
  } else if (drugType === DrugTypeEnum.Zolmitriptan && dosageType === DosageTypeEnum.SprayMg) {
    return "Zomig Nasal Spray"
  }
  if (drugType === DrugTypeEnum.GalcanezumabGnlm) {
    return "Emgality®"
  }

  return upperFirst(drugType)
}

export const displayedDosageType = (dosageType: Maybe<DosageTypeEnum>) => {
  if (dosageType === DosageTypeEnum.OdtMg) {
    return "Orally dissolving tablet"
  }

  if (dosageType === DosageTypeEnum.SprayMg) {
    return "Nasal Spray"
  }

  if (dosageType === DosageTypeEnum.InjectableMg) {
    return "Injection"
  }

  return dosageType?.replace(/_/g, "/").toUpperCase()
}

export const displayedDrugCategory = (drugCategory: DrugCategoryEnum, drugType?: DrugTypeEnum) => {
  if (drugType === DrugTypeEnum.Rimegepant) {
    return "Fast-acting"
  }

  /* TODO: Remove when `preventative` changed to `preventive` */
  if (drugCategory === DrugCategoryEnum.Preventative) {
    return "Preventive"
  }

  if (drugCategory === DrugCategoryEnum.Otc) {
    return "Supplement"
  }

  return drugCategory.replace(/_/g, "-")
}

export const calculateNextBillingDate = (productLineItems: GetPricingLineItems[]): string => {
  const minSupplyInDays = min(
    productLineItems
      .filter(({ product }) => !!product && product.daysUntilFirstRefill)
      .map(({ product }) => product!.daysUntilFirstRefill)
  )

  return moment().add(minSupplyInDays, "days").format("MMMM DD, YYYY")
}

export enum CheckoutType {
  ltddOnly = "ltddOnly",
  v3OTCOnly = "v3OTCOnly",
  v3 = "v3",
  emgalityOnly = "emgalityOnly"
}

export const getCheckoutType = (cartItems: Product[]): CheckoutType => {
  if (cartItems.length === 0) {
    return CheckoutType.ltddOnly
  } else if (cartItems.filter(product => product.needsPrescription).length === 0) {
    return CheckoutType.v3OTCOnly
  } else if (cartItems.find(p => p.drugType === DrugTypeEnum.GalcanezumabGnlm && cartItems.length === 1)) {
    return CheckoutType.emgalityOnly
  }

  return CheckoutType.v3
}

export const billingCadenceInMonths = (product?: Pick<Product, "supplyInDays">): number =>
  Math.floor((product && product.supplyInDays) || 30) / 30

// Selects version of the product with no dosage and amount (..unless if it's OTC or migraine relief)
export const getDefaultProduct = (productGroup: ProductGroup): Product | undefined =>
  productGroup.drugCategory === DrugCategoryEnum.Otc ||
  productGroup.drugType === DrugTypeEnum.MigraineRelief ||
  productGroup.drugType === DrugTypeEnum.Nerivio ||
  productGroup.drugType === DrugTypeEnum.Rimegepant ||
  productGroup.drugType === DrugTypeEnum.Diclofenac ||
  productGroup.drugType === DrugTypeEnum.GalcanezumabGnlm ||
  (productGroup.drugType === DrugTypeEnum.Zolmitriptan && productGroup.dosageType === DosageTypeEnum.SprayMg)
    ? productGroup.products[0]
    : productGroup.products.find(p => !p.dosageValue && !p.amountValue)

export const getPriceAndDiscountPrice = (
  product: Maybe<GetPricingProduct>,
  amount: number,
  amountWithDiscount: number,
  journeyKind?: JourneyKindEnum
) => {
  const recurringPrice = product && product.recurringProduct && product.recurringProduct.price
  // renewal journey does not support get started offer discounts right now
  if (recurringPrice && journeyKind !== JourneyKindEnum.CoveRenewal) {
    return {
      price: recurringPrice,
      discountPrice: amount
    }
  } else if (recurringPrice && journeyKind === JourneyKindEnum.CoveRenewal) {
    return {
      price: recurringPrice,
      discountPrice: recurringPrice
    }
  } else {
    return {
      price: amount,
      discountPrice: amountWithDiscount
    }
  }
}

export const getFormatDiscountPrice = ({
  discountPercentage,
  withPercent
}: {
  discountPercentage: Maybe<number>
  withPercent?: boolean
}) => {
  const discount = (100 * (discountPercentage ?? 0.5)).toString()

  return `${discount}${withPercent ? "%" : ""}`
}

export const drugFactsHref = (drugType: Maybe<DrugTypeEnum>, dosageType: Maybe<DosageTypeEnum>) => {
  const drugFact = PRODUCT_DRUG_FACT_PATHS.find(p => p.drugType === drugType && p.dosageType === dosageType)

  if (drugFact) {
    return "/drug-facts" + drugFact.contentfulPath
  }

  switch (drugType) {
    case DrugTypeEnum.Nerivio:
      return s3CoveUrl("/drug-facts/nerivio.pdf")
    case DrugTypeEnum.Coq10:
      return s3CoveUrl("/drug-facts/coq10.pdf")
    case DrugTypeEnum.Riboflavin:
      return s3CoveUrl("/drug-facts/riboflavin.pdf")
    case DrugTypeEnum.Magnesium:
      return s3CoveUrl("/drug-facts/magnesium.pdf")
    case DrugTypeEnum.Beam:
      return s3CoveUrl("/drug-facts/beam.pdf")
    default:
      return "/drug-facts"
  }
}

export const getProductIdsFromSubscriptions = (
  subscriptions: ActiveSubscriptionsFragmentActiveSubscriptions[]
): number[] => subscriptions.map(subscription => subscription.activeProducts.map(product => product.id)).flat()

export const removeRefillFromDescription = (description: string) => {
  if (!description.includes("Refill")) {
    return description
  }

  const [productDescription] = description.split("Refill")

  return productDescription.substring(0, productDescription.length - 1)
}

export const useAllVariationsForProduct = (drugType: DrugTypeEnum, dosageType: DosageTypeEnum) => {
  const { productGroups } = useProductsState()
  const productGroup = productGroups.find(pg => pg.drugType === drugType && pg.dosageType === dosageType)

  if (!productGroup) {
    throw new Error(`No product group for ${drugType}, ${dosageType}`)
  }

  const uniqBySupplyInDays = uniqBy(productGroup.products, product => product.supplyInDays)

  return {
    oneMonthSupplyProduct: uniqBySupplyInDays.find(p => p.supplyInDays === 30),
    threeMonthSupplyProduct: uniqBySupplyInDays.find(p => p.supplyInDays === 90)
  }
}

export const getProductTooltip = (drugType: DrugTypeEnum, dosageType: DosageTypeEnum): string | undefined => {
  if (drugType === DrugTypeEnum.Rimegepant) {
    return "Nurtec ODT is an orally dissolving tablet."
  } else if (dosageType === DosageTypeEnum.OdtMg) {
    if (drugType === DrugTypeEnum.Ondansetron) {
      return "This is an orally dissolving form of ondansetron."
    } else if (drugType === DrugTypeEnum.Rizatriptan) {
      return "This is an orally dissolving form of rizatriptan."
    }
  }

  return undefined
}

export function filterMedicalFee<ProductType extends Pick<Product, "drugType">>(products: ProductType[]) {
  return products.filter(p => p.drugType !== DrugTypeEnum.MedicalFee)
}

export const filterProductLineItems = (lineItems: ExtendedLineItems[]) =>
  lineItems.filter(
    lineItem => lineItem.kind === LineItemKindEnum.Product && lineItem.product?.drugType !== DrugTypeEnum.MedicalFee
  )

export const getActiveRxMedications = (userProducts: Product[]) => {
  let activeRxMedications = ""
  const filteredUserProducts = userProducts.filter(
    p => !p.drugType || p!.dosageType || !p.needsPrescription || p.drugType !== DrugTypeEnum.MedicalFee
  )
  if (filteredUserProducts.length === 0) {
    throw Error("User has no RX products to renew.")
  } else if (filteredUserProducts.length === 1) {
    activeRxMedications += displayedProductName(filteredUserProducts[0].drugType, filteredUserProducts[0].dosageType)
  } else {
    filteredUserProducts.forEach((p, idx) => {
      if (filteredUserProducts.length > 2 && idx === filteredUserProducts.length - 2) {
        // before last product but more then two products in array
        activeRxMedications += displayedProductName(p.drugType, p.dosageType) + ", and "
      } else if (idx === filteredUserProducts.length - 2) {
        // before last product
        activeRxMedications += displayedProductName(p.drugType, p.dosageType) + " and "
      } else if (idx === filteredUserProducts.length - 1) {
        // last product
        activeRxMedications += displayedProductName(p.drugType, p.dosageType)
      } else {
        // comma-delimit
        activeRxMedications += displayedProductName(p.drugType, p.dosageType) + ", "
      }
    })
  }

  return activeRxMedications
}

/* We want to order products in a specific way on /our-offering /build-your-plan */
export const getProductOrder = (drugType: DrugTypeEnum) => {
  switch (drugType) {
    case DrugTypeEnum.Magnesium:
    case DrugTypeEnum.Rimegepant:
      return 0
    case DrugTypeEnum.Sumatriptan:
      return 1
    case DrugTypeEnum.Rizatriptan:
      return 2
    case DrugTypeEnum.Diclofenac:
      return 3
    case DrugTypeEnum.Nerivio:
      return 4
    case DrugTypeEnum.Naratriptan:
      return 5
    case DrugTypeEnum.Zolmitriptan:
      return 6
    case DrugTypeEnum.Eletriptan:
      return 7
    case DrugTypeEnum.MigraineRelief:
      return 8
    default:
      return 0
  }
}

export const getSortedProductGroups = (productGroups: ProductGroup[], getOrder: (drugType: DrugTypeEnum) => number) =>
  productGroups
    .map(p => ({
      ...p,
      order: getOrder(p.drugType)
    }))
    .sort((a, b) => a.order - b.order)

export const getInsuranceProducts = () => [DrugTypeEnum.Rimegepant, DrugTypeEnum.GalcanezumabGnlm]

export const EXCLUDED_DRUG_TYPES = [DrugTypeEnum.Beam]
